import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const Navbar = () => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Retrieve user info from localStorage
  const userInfo = localStorage.getItem('userInfo') 
    ? JSON.parse(localStorage.getItem('userInfo')) 
    : null;

  const logoutHandler = () => {
    localStorage.removeItem('userInfo');
    navigate('/login');
    setIsMobileOpen(false);
  };

  // Dynamic Command Center Search Trigger
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await axios.get(`/api/products?keyword=${searchQuery}&limit=8`);
        setSearchResults(data.products || []);
      } catch (error) {
        console.error('Error fetching search results', error);
      }
      setSearchLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Close search on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch]);

  // --- STYLES ---
  const navBarStyle = {
    backgroundColor: 'var(--nav-bg)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '25px 0',
    borderBottom: '1px solid var(--border-light)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    position: 'sticky',
    top: 0,
    zIndex: 9999,
    width: '100%',
    boxSizing: 'border-box'
  };

  const brandStyle = {
    color: 'var(--nav-text)',
    fontSize: '50px',
    fontWeight: '900',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    fontFamily: '"The Seasons", "The Seasons Regular", serif',
    padding: '10px 30px',
    textDecoration: 'none',
    transition: '0.3s ease',
    marginBottom: '20px'
  };

  const linkStyle = (id) => ({
    color: isHovered === id ? 'var(--accent-yellow, #ffcc00)' : 'var(--nav-text)',
    fontWeight: '700',
    fontSize: '13px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    padding: '10px 15px',
    textDecoration: 'none',
    transition: '0.2s ease-in-out',
    cursor: 'pointer',
    transform: isHovered === id ? 'translateY(-2px)' : 'translateY(0)',
    display: 'inline-block'
  });

  const userBoxStyle = {
    border: '2px solid var(--accent-yellow, #ffcc00)',
    padding: '8px 18px',
    color: 'var(--accent-yellow, #ffcc00)',
    fontWeight: '900',
    fontSize: '12px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    transition: '0.3s'
  };

  return (
    <>
      <nav className="custom-navbar" style={navBarStyle}>
      <style>
        {`
          .mobile-toggle-btn { display: none; }
          .nav-links-wrapper { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; gap: 15px; width: 100%; transition: all 0.3s ease; }
          
          @media (max-width: 768px) {
            .custom-navbar { padding: 15px 20px !important; flex-direction: row !important; justify-content: space-between !important; }
            .brand-logo { margin-bottom: 0 !important; font-size: 18px !important; padding: 6px 15px !important; letter-spacing: 4px !important; }
            
            .mobile-toggle-btn { display: block; background: transparent; border: none; outline: none; color: #fff; padding: 6px 12px; cursor: pointer; font-size: 1.5rem; transition: 0.3s; }
            .mobile-toggle-btn:hover { background: transparent; color: var(--accent-yellow, #ffcc00); }
            
            .nav-links-wrapper { 
              display: none; 
              flex-direction: column; 
              position: absolute; 
              top: 100%; 
              left: 0; 
              background-color: var(--nav-bg); 
              padding: 30px 0; 
              border-bottom: 2px solid var(--border-main); 
              gap: 20px !important;
              box-shadow: 0 10px 20px rgba(0,0,0,0.5);
            }
            .nav-links-wrapper.open { display: flex; }
            .nav-links-wrapper > a, .nav-links-wrapper > span { font-size: 16px !important; width: 100%; text-align: center; }
            .auth-section { flex-direction: column; width: 100%; gap: 15px !important; margin-left: 0 !important; margin-top: 10px; }
            .auth-section > a, .auth-section > span { display: flex; justify-content: center; width: 100%; }
            
            .search-overlay { padding: 40px 5% !important; }
            .search-overlay input { font-size: 1.2rem !important; padding: 15px 15px 15px 35px !important; }
            .search-overlay .fa-search { font-size: 1rem !important; }
          }
        `}
      </style>

      {/* BRAND LOGO */}
      <Link 
        to="/" 
        className="brand-logo"
        style={brandStyle}
        onClick={() => setIsMobileOpen(false)}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'var(--accent-yellow, #ffcc00)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#fff';
        }}
      >
        GOAT INDIA
      </Link>

      {/* MOBILE TOGGLE BUTTON */}
      <button className="mobile-toggle-btn" onClick={() => setIsMobileOpen(!isMobileOpen)}>
        <i className={isMobileOpen ? "fas fa-times" : "fas fa-bars"}></i>
      </button>

      {/* NAVIGATION LINKS */}
      <div className={`nav-links-wrapper ${isMobileOpen ? 'open' : ''}`}>
        <Link to="/" style={linkStyle('shop')} onClick={() => setIsMobileOpen(false)} onMouseEnter={() => setIsHovered('shop')} onMouseLeave={() => setIsHovered(null)}>
          Shop
        </Link>
        
        <Link to="/about" style={linkStyle('about')} onClick={() => setIsMobileOpen(false)} onMouseEnter={() => setIsHovered('about')} onMouseLeave={() => setIsHovered(null)}>
          About Us
        </Link>
        
        <Link to="/our-story" style={linkStyle('story')} onClick={() => setIsMobileOpen(false)} onMouseEnter={() => setIsHovered('story')} onMouseLeave={() => setIsHovered(null)}>
          Our Story
        </Link>

        <span style={linkStyle('search')} onClick={() => { setShowSearch(true); setIsMobileOpen(false); }} onMouseEnter={() => setIsHovered('search')} onMouseLeave={() => setIsHovered(null)}>
          Search 🔍
        </span>
        
        <Link to="/cart" style={{ ...linkStyle('cart'), color: isHovered === 'cart' ? '#ffcc00' : '#fff' }} onClick={() => setIsMobileOpen(false)} onMouseEnter={() => setIsHovered('cart')} onMouseLeave={() => setIsHovered(null)}>
          Cart 🛒
        </Link>
        
        <Link to="/contact" style={linkStyle('contact')} onClick={() => setIsMobileOpen(false)} onMouseEnter={() => setIsHovered('contact')} onMouseLeave={() => setIsHovered(null)}>
          Contact
        </Link>

        {/* AUTH SECTION */}
        {userInfo ? (
          <div className="auth-section" style={{ display: 'flex', alignItems: 'center', gap: '20px', marginLeft: '10px' }}>
            <Link to="/profile" style={{ textDecoration: 'none' }} onClick={() => setIsMobileOpen(false)}>
              <div 
                style={userBoxStyle}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 204, 0, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <i className="fas fa-user"></i> {userInfo.name}
              </div>
            </Link>

            <span 
              onClick={logoutHandler} 
              style={{ ...linkStyle('logout'), color: '#ff4444', fontSize: '11px' }}
              onMouseEnter={() => setIsHovered('logout')}
              onMouseLeave={() => setIsHovered(null)}
            >
              Logout
            </span>
          </div>
        ) : (
          <Link 
            to="/login" 
            style={{ ...linkStyle('login'), border: '2px solid #fff', padding: '8px 20px', marginLeft: '10px' }} 
            onClick={() => setIsMobileOpen(false)}
            onMouseEnter={() => setIsHovered('login')}
            onMouseLeave={() => setIsHovered(null)}
          >
            Login
          </Link>
        )}
      </div>
      </nav>

      {/* GLOBAL SEARCH COMMAND CENTER OVERLAY */}
      {showSearch && (
        <div className="search-overlay" style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          backgroundColor: 'var(--bg-blueprint)',
          backgroundImage: 'linear-gradient(to right, var(--grid-line) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 10000, color: 'var(--text-main)',
          display: 'flex', flexDirection: 'column', padding: '80px 10%',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <style>
            {`
              @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
              @keyframes terminalBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
              @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
              .terminal-cursor { animation: terminalBlink 1s step-end infinite; color: var(--accent-red); display: inline-block; }
              .search-result-card { border: 3px solid transparent; transition: all 0.2s; background: var(--bg-surface); padding: 15px; cursor: pointer; }
              .search-result-card:hover { border: 3px solid var(--border-main); transform: translateY(-5px); box-shadow: 8px 8px 0px var(--shadow-main); }
            `}
          </style>

          <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} style={{
            position: 'absolute', top: '30px', right: '50px', background: 'var(--btn-bg)', border: 'none',
            color: 'var(--btn-text)', fontSize: '24px', cursor: 'pointer', fontWeight: '900', transition: '0.3s',
            width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '0'
          }} onMouseEnter={(e) => e.target.style.background = 'var(--accent-red)'} onMouseLeave={(e) => e.target.style.background = 'var(--btn-bg)'}>✕</button>
          
          <div style={{ position: 'relative', margin: '20px auto 60px auto', width: '100%', maxWidth: '800px' }}>
            <span style={{ 
              position: 'absolute', left: '0', top: '50%', transform: 'translateY(-50%)', 
              fontSize: '1.8rem', color: 'var(--text-main)', fontWeight: '900', marginLeft: '20px'
            }}>
              <i className="fas fa-search"></i>
            </span>
            <input 
              type="text" 
              placeholder="INITIATE SEARCH QUERY..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%', background: 'var(--bg-surface)', border: '4px solid var(--border-main)',
                color: 'var(--text-main)', fontSize: 'clamp(1.2rem, 3vw, 2rem)', fontWeight: '900',
                outline: 'none', textTransform: 'uppercase', padding: '20px 20px 20px 70px',
                boxShadow: '8px 8px 0px var(--shadow-main)', transition: 'all 0.3s ease'
              }}
              autoFocus
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                style={{ 
                  position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', 
                  background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', fontWeight: '900', 
                  width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: '0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-red)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--btn-bg)'}
              >✕</button>
            )}
          </div>

          <div className="hide-scrollbar" style={{ overflowY: 'auto', flexGrow: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '30px' }}>
            {searchLoading ? (
              <h3 style={{ fontWeight: '900', letterSpacing: '2px', color: 'var(--text-main)', gridColumn: '1 / -1', textAlign: 'center', marginTop: '50px' }}>
                SCANNING ARCHIVE <span className="terminal-cursor">█</span>
              </h3>
            ) : searchResults.length > 0 ? (
              searchResults.map((product, index) => (
                <div key={product.id} className="search-result-card" onClick={() => { setShowSearch(false); setSearchQuery(''); navigate(`/product/${product.id}`); }} style={{ animation: `slideUp 0.3s ease-out forwards`, animationDelay: `${index * 0.05}s`, opacity: 0 }}>
                  <img src={product.image} alt={product.name} style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', border: '2px solid var(--border-main)' }} />
                  <div style={{ marginTop: '15px', fontWeight: '900', fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '2px solid var(--border-main)', paddingBottom: '10px' }}>{product.name}</div>
                  <div style={{ color: 'var(--accent-red)', fontWeight: '900', fontSize: '16px', marginTop: '10px' }}>₹{product.price}</div>
                </div>
              ))
            ) : searchQuery.trim() ? (
              <h3 style={{ fontWeight: '900', letterSpacing: '2px', color: 'var(--text-muted)', gridColumn: '1 / -1', textAlign: 'center', marginTop: '50px' }}>NO MATCHES FOUND IN ARCHIVE.</h3>
            ) : (
              <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                <h4 style={{ fontWeight: '900', color: 'var(--text-muted)', letterSpacing: '2px', marginBottom: '20px', fontSize: '12px', textTransform: 'uppercase' }}>SUGGESTED QUERIES</h4>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  {['T-shirt', 'Hoodie', 'Sweatshirt'].map(tag => (
                    <button 
                      key={tag} 
                      onClick={() => setSearchQuery(tag)} 
                      style={{ background: 'transparent', border: '2px solid var(--border-main)', color: 'var(--text-main)', padding: '8px 15px', fontWeight: '900', cursor: 'pointer', transition: 'all 0.2s', fontSize: '12px', textTransform: 'uppercase' }} 
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--text-main)'; e.currentTarget.style.color = 'var(--bg-surface)'; e.currentTarget.style.transform = 'translate(-2px, -2px)'; e.currentTarget.style.boxShadow = '4px 4px 0px var(--shadow-main)'; }} 
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-main)'; e.currentTarget.style.transform = 'translate(0, 0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', fontWeight: '900', fontSize: '11px', letterSpacing: '3px', color: '#aaa', pointerEvents: 'none' }}>
            [ ESC TO ABORT ]
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;